import React from "react";

import { useGetUserExperienceFlagsQuery, useGetLifePlansQuery } from "EmployerApplication/api";
import AddMedicalPlansBox from "EmployerApplication/Components/FormSections/AddMedicalPlansBox";
import ShoppingCartPlanCardV2 from "EmployerApplication/Components/FormSections/ShoppingCartPlanCardV2";
import { cleanEmployeeProfile } from "EmployerApplication/CostEstimationUtils";
import { useDisabilityEnabled } from "EmployerApplication/hooks";
import { CoverageOptionsPageContext } from "EmployerApplication/pages/CoverageOptionsPage";
import { quotePageURL, isRenewal } from "QuotePage/urls";
import { useSelector, useDispatch } from "react-redux";
import _, { isEmpty, compact } from "underscore";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { PlanCardSkeleton } from "Cobalt/Skeleton";
import { getCart, getSelectedMedicalPlans, getSelectedDentalPlan, getSelectedVisionPlan, addPlanToCart } from "Common/CartService";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import InfoPopover from "Common/InfoPopover";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import { Body2 } from "Theme/BodyText";
import { useThemeContext, ThemeProvider } from "Theme/Context";

import AddAncillaryPlanBox from "./shoppingCartSection/AddAncillaryPlanBox";
import { useIsEligibleForLifePlans, LifePlanEligibilityMessage } from "./shoppingCartSection/LifePlanEligibility";

const VisionPlanBox = ({ isSampleQuote }) => {
  const visionPlan = useSelector((state) => getSelectedVisionPlan(state));

  return visionPlan ? <ShoppingCartPlanCardV2 plan={visionPlan} planType="vision" isSampleQuote={isSampleQuote} itemTitle="Vision" /> : <PlanCardSkeleton />;
};

const DentalPlanBox = ({ isSampleQuote }) => {
  const dentalPlan = useSelector((state) => getSelectedDentalPlan(state));

  return dentalPlan ? <ShoppingCartPlanCardV2 plan={dentalPlan} planType="dental" isSampleQuote={isSampleQuote} itemTitle="Dental" /> : <PlanCardSkeleton />;
};

const LifePlanBox = ({ isSampleQuote }) => {
  const companyIDToken = useSelector((state) => state.companyProfile?.idToken);
  const useEmployeeForms = isRenewal();
  const companyZipCode = useSelector((state) => state.companyProfile?.zip_code_number);

  const employeeProfile = useSelector((state) => state.employeeProfile);
  const targetEffectiveDate = useSelector((state) => state.companyProfile?.targetEffectiveDate);

  const { data: lifePlanData, isFetching: isFetchingLifePlans } = useGetLifePlansQuery({
    companyIDToken,
    useEmployeeForms,
    companyZipCode,
    employeeProfile,
    targetEffectiveDate,
  });

  const lifePlanSelected = useSelector((state) => state.cart.life);
  const lifePlans = lifePlanData?.results;
  const matchedLifePlan = _.findWhere(lifePlans, { id: lifePlanSelected });
  const dispatch = useDispatch();

  if (lifePlanSelected && lifePlans?.length && !matchedLifePlan) {
    // clear selected life plan from cart if it's not found in the life plans list
    AnalyticsTracking.trackEvent("life_plan_not_found", {
      label: "life_plan_not_found",
      location: "shopping_cart",
      life_plan_in_cart: lifePlanSelected,
      life_plans: lifePlans?.map((plan) => plan?.id),
    });

    AnalyticsTracking.trackEvent("plan_removed", {
      type: "life",
      plan_id: lifePlanSelected,
    });

    dispatch(addPlanToCart({ planType: "life", plan: null }));
  }

  return matchedLifePlan && !isFetchingLifePlans ? (
    <ShoppingCartPlanCardV2 plan={matchedLifePlan} planType="life" isSampleQuote={isSampleQuote} itemTitle="Life" />
  ) : (
    <PlanCardSkeleton />
  );
};

const AddDentalPlan = () => (
  <CoverageOptionsPageContext.Consumer>
    {({ showDentalModal }) => (
      <AddAncillaryPlanBox
        onClick={() => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            text: "Add Dental",
            location: "shopping_cart",
          });

          showDentalModal();
        }}
        title="Add Dental"
      />
    )}
  </CoverageOptionsPageContext.Consumer>
);

const AddVisionPlan = () => (
  <CoverageOptionsPageContext.Consumer>
    {({ showVisionModal }) => (
      <AddAncillaryPlanBox
        onClick={() => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            text: "Add Vision",
            location: "shopping_cart",
          });

          showVisionModal();
        }}
        title="Add Vision"
      />
    )}
  </CoverageOptionsPageContext.Consumer>
);

const AddLifePlan = () => (
  <CoverageOptionsPageContext.Consumer>
    {({ showLifeModal }) => (
      <AddAncillaryPlanBox
        onClick={() => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            text: "Add Life",
            location: "shopping_cart",
          });

          showLifeModal();
        }}
        title="Add Life"
      />
    )}
  </CoverageOptionsPageContext.Consumer>
);

const LifeEligilityBox = () => (
  <InfoPopover
    infoText={LifePlanEligibilityMessage}
    displayComponent={
      <div>
        <AddAncillaryPlanBox title="Add Life" disabled />
      </div>
    }
  />
);

export const ShoppingCartSection = ({ carrierMax }) => {
  const { intuit, cobalt } = useThemeContext();

  const handleChangeMedicalPlan = () => {
    window.location = appendAuthParams(quotePageURL());
  };

  const selectedMedicalPlans = useSelector(getSelectedMedicalPlans);
  const employeeProfile = useSelector((state) => state.employeeProfile);
  const relatedCartPlanIds = useSelector((state) => state.relatedCartPlanIds);
  const { medical: medicalPlansInCart, dental: dentalPlanInCart, vision: visionPlanInCart, life: lifePlanInCart } = useSelector(getCart);
  const medicalPlanOptionIDs = (relatedCartPlanIds || []).filter((n) => !medicalPlansInCart.includes(n));

  const companyIDToken = useSelector((state) => state.companyProfile?.idToken);
  const { data: userExperienceFlags } = useGetUserExperienceFlagsQuery({
    companyIDToken,
  });

  const hasAddedLifePlan = userExperienceFlags?.added_life_plan;

  const lifePlanEligible = useIsEligibleForLifePlans();
  const { enabled: inDisabilityABTest, isLoading } = useDisabilityEnabled();
  const shouldShowLifePlanTrigger = !isLoading && !inDisabilityABTest;

  let addedMedicalPlans = null;
  let addAdditionalMedicalPlan = null;

  const medicalCart = compact(medicalPlansInCart);

  const isSampleQuote = !(cleanEmployeeProfile(employeeProfile).length > 0);

  const medicalSectionTitle = "Medical";

  if (isEmpty(medicalCart)) {
    addedMedicalPlans = (
      <div className="empty-state">
        <div className="heading3">You Haven't Chosen A Medical Plan Yet.</div>
        <div className="continue-shopping-link">
          <a onClick={handleChangeMedicalPlan.bind(this)}>Click here to continue shopping for Medical Plans</a>
        </div>
      </div>
    );
  } else if (medicalCart.length >= 1 && selectedMedicalPlans.length === 0) {
    addedMedicalPlans = <PlanCardSkeleton />;
  } else if (medicalCart.length >= 1 && selectedMedicalPlans.length >= 1) {
    addedMedicalPlans = selectedMedicalPlans.map((plan, i) => (
      <ShoppingCartPlanCardV2
        plan={plan}
        planType="medical"
        key={i}
        lastPlan={medicalCart.length == 1}
        isSampleQuote={isSampleQuote}
        showAverageCost={medicalCart.length >= 2}
        itemTitle={i === 0 ? medicalSectionTitle : null}
        index={i}
      />
    ));
  }

  if (medicalCart.length < carrierMax && medicalCart.length >= 1 && medicalPlanOptionIDs.length >= 1 && selectedMedicalPlans[0].target_market != "mec") {
    addAdditionalMedicalPlan = (
      <Div mb={4}>
        <AddMedicalPlansBox carrierMax={carrierMax} />
      </Div>
    );
  } else {
    addAdditionalMedicalPlan = null;
  }

  return (
    <Row>
      <Col>
        <Div className="plans" data-testid="ShoppingCartSection__Plans">
          {/* Theme provider hack to get Cart Page Plan cards to match Quote Page (non-cobalt) */}
          <ThemeProvider cobalt={false} intuit={intuit} classic>
            {addedMedicalPlans}
            {addAdditionalMedicalPlan}
            {dentalPlanInCart ? <DentalPlanBox isSampleQuote={isSampleQuote} /> : null}
            {visionPlanInCart ? <VisionPlanBox isSampleQuote={isSampleQuote} /> : null}
            {lifePlanInCart && shouldShowLifePlanTrigger ? <LifePlanBox isSampleQuote={isSampleQuote} /> : null}
          </ThemeProvider>
        </Div>
        <Div className="Cart--DentalVisionButtons" mb={4}>
          {!dentalPlanInCart ? <AddDentalPlan /> : null}
          {!visionPlanInCart ? <AddVisionPlan /> : null}
          {lifePlanEligible && !lifePlanInCart && shouldShowLifePlanTrigger ? <AddLifePlan /> : null}
          {!lifePlanEligible && hasAddedLifePlan ? <LifeEligilityBox /> : null}
        </Div>
        {isSampleQuote && (
          <Div className="IsSampleQuote--Note">
            <Body2>
              <sup>*</sup>The initial quote is for a 30 year-old employee.
            </Body2>
          </Div>
        )}
      </Col>
    </Row>
  );
};

export default ShoppingCartSection;
